// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/**
 * The `event_type` that declares a release-gate kind and the window for it.
 * PLATFORM selector vocabulary in the 64.153.0 tradition (`rights_request_*`,
 * `authority_direction_response`, `sector_licence_lifecycle`): packs declare it
 * on deadline records, and nothing in `src/` branches on a pack's instrument,
 * authority or `sla_type` strings.
 */
export const PUBLICATION_RELEASE_GATE_EVENT_TYPE = 'publication_release_gate';
export const PUBLICATION_PREREQUISITE_RECORDS_COLLECTION = 'grc_publication_prerequisite_records';
/**
 * The `event_type` that declares the category vocabulary a publication may be
 * tagged with (AC3): `request_types` are the category codes,
 * `request_type_labels` their bilingual names, and the record's `reference` and
 * `note` the citation and caveat rendered beside them. A pack that declares
 * none leaves the vocabulary EMPTY, which is the honest first state — not a
 * reason to fall back to a list in `src/`.
 */
export const PUBLICATION_ASSESSMENT_CATEGORY_EVENT_TYPE = 'publication_assessment_category';
/**
 * The `event_type` that names the questionnaire recording whether a service is
 * covered (AC5). The declaring record's `linked_form_id` is the template to
 * read; the platform never carries that identifier itself.
 *
 * A form category and a framework code together are NOT enough to find it: the
 * publishing overlay ships three `assessment`-category templates against one
 * framework, so selecting on that pair returns three. Naming the template from
 * installed content is what keeps its identifier out of `src/` (R-46), and a
 * pack that declares no such record leaves the questionnaire simply
 * undiscovered — stated as such, never guessed at by matching a title.
 */
export const COVERED_SCOPE_QUESTIONNAIRE_EVENT_TYPE = 'covered_scope_questionnaire';
/**
 * Which way a declared window runs from its anchor. `before` counts BACK from
 * the planned release date; `after` counts forward from the deposit receipt.
 * The same figure yields both derived dates, which is why one record can be
 * read in both directions — see `PublicationDerivedDate`.
 *
 * Absent or outside this list on a pack record is read as `null` and rendered
 * as an unclassified window, never silently defaulted to either direction.
 */
export const PUBLICATION_WINDOW_DIRECTIONS = ['before', 'after'];
/**
 * Tags on guidance records (`grc_regulatory_guidances`) the screens select to
 * render the pack's own statements. Both are the 62.19.0 tags, reused so one
 * `BoundarySection` serves this capability too (R-43): AC9's three statements —
 * that the platform performs no statutory content assessment, does not act as
 * the required representative, and does not guarantee that content is lawful —
 * are pack text under `platformBoundary`, never platform copy.
 */
export const PUBLICATION_GUIDANCE_TAGS = {
    platformBoundary: 'platform_boundary',
    scopeBoundary: 'scope_boundary',
};
/**
 * fix-sprint-20260904-2130-stream-remainders F1 (owner ruling Q-SR1 (b), 2026-10-20) — the per-step
 * status is the platform-generic pair: a step either carries a person's recording
 * or it does not. The platform derives no verdict about the step.
 */
export const PUBLICATION_STEP_STATUSES = ['recorded', 'not_recorded'];
// ── The two derived dates (AC1) ──
/**
 * The two dates one figure yields, each labelled, each citing the same pack
 * record. They are DIFFERENT questions with different anchors, which is why
 * they are separate rows rather than one date with two meanings:
 *
 * - `latest_permitted_deposit` — planned release MINUS the figure. "By when
 *   must the deposit be made for this release date to stand?"
 * - `earliest_permitted_release` — receipt PLUS the figure. "From when may this
 *   publication be released?" This is the date the server-side gate reads.
 */
export const PUBLICATION_DERIVED_DATE_KINDS = [
    'latest_permitted_deposit',
    'earliest_permitted_release',
];
export const PUBLICATION_DERIVED_DATE_STATES = ['dated', 'undated', 'blocked'];
/**
 * Why a derived date carries no date. Every value is a DIFFERENT statement —
 * the 62.14.0 `DirectionClockState` lesson — and none of them is a silent
 * blank:
 *
 * - `no_installed_window_for_kind` — no installed pack declares a window for
 *   this gate kind, so there is no figure to count with. Not a platform fault;
 * - `no_planned_release_date` — the publication records no planned release
 *   date, so the backwards count has no anchor;
 * - `no_deposit_receipt` — no deposit submission with a receipt is recorded, so
 *   the forwards count has no anchor;
 * - `clock_not_in_force` — the declaring framework does not bind yet;
 *   `activation_date` says when it will;
 * - `framework_record_unreadable` — the framework record could not be read, so
 *   no statement about its timing is made;
 * - `declared_by_reference_install` (Sprint 62.25.0, owner ruling OD8 (a)) — an
 *   installed framework DOES declare a window for this kind, and the
 *   organisation installed it for reference only, so its clock is not run.
 *   Never reported as `no_installed_window_for_kind`;
 * - `declaring_framework_not_operational` (Sprint 62.25.0) — an installed
 *   framework declares one and the install-mode policy excludes it for another
 *   reason (superseded, retired).
 *
 * The two anchor reasons are deliberately separate: "you have not recorded a
 * planned release date" and "you have not recorded a deposit receipt" are
 * different remedies, and one date may be dated while the other is not.
 */
export const PUBLICATION_DERIVED_DATE_UNDATED_REASONS = [
    'no_installed_window_for_kind',
    'no_planned_release_date',
    'no_deposit_receipt',
    'clock_not_in_force',
    'framework_record_unreadable',
    'declared_by_reference_install',
    'declaring_framework_not_operational',
];
/**
 * A window IS declared and the platform cannot compute a date from it:
 * `unsupported_unit` names a unit the platform genuinely cannot compute,
 * `no_numeric_window` a declaring record that carries no figure at all. Both
 * render the pack's own figure and unit as text instead of a date.
 */
export const PUBLICATION_DERIVED_DATE_BLOCKED_REASONS = [
    'unsupported_unit',
    'no_numeric_window',
];
// ── Publications (AC1, AC3, AC8) ──
/**
 * The organisation's own account of where a publication stands. `releasable` is
 * the state the server-side gate grants; `released` is the operator's record
 * that it went out. There is deliberately no approval state between them (AC2).
 */
export const PUBLICATION_STATUSES = [
    'draft',
    'deposit_recorded',
    'releasable',
    'released',
    'withdrawn',
];
/**
 * The evidence an inspector's question "what rights did you hold when you
 * deposited this" is answered from. It attaches to the PUBLICATION, not to the
 * assessment or the submission, so there is one answer visible from all three
 * (AC8). `attachment_ref` is required: an evidence entry with nothing attached
 * is a claim, not evidence.
 */
export const PUBLICATION_RIGHTS_EVIDENCE_KINDS = [
    'copyright',
    'licence',
    'content_provenance',
];
// ── The release gate (AC2) ──
export const PUBLICATION_RELEASE_OUTCOMES = ['permitted', 'refused'];
/**
 * Why the gate refused. Each is a different fact about the record, so the
 * refusal names its own remedy:
 *
 * - `no_installed_window_declared` — no installed pack declares a window for
 *   this gate kind. The gate still REFUSES (fail closed) and cites nothing: a
 *   gate that cannot see is not a gate;
 * - `no_deposit_submission_recorded` — nothing has been deposited;
 * - `no_deposit_receipt_recorded` — a submission exists with no receipt, so the
 *   window has no anchor to run from;
 * - `deposit_window_not_elapsed` — the derived earliest permitted release date
 *   is in the future, in the organisation's own calendar;
 * - `window_not_computable` — a window IS declared and the platform can compute
 *   no date from its figure and unit. Refused rather than waved through, and
 *   the pack's own figure is shown as text.
 *
 * The five above are facts about the WINDOW, and each is carried on a
 * `PUBLICATION_RELEASE_REFUSED` 409. The sixth is a fact about the RECORD:
 *
 * - `not_awaiting_gate` — the publication was already `releasable`, `released`
 *   or `withdrawn`, so the gate never read a window and never ruled on one.
 *   It is delivered as `PUBLICATION_ALREADY_RELEASABLE`, and it exists because
 *   AC2's trail is every ATTEMPT, refused or permitted alike — not every
 *   attempt the window ruled on. Gate E12.5 pass 1 (B8) measured three of the
 *   six refusal paths leaving no attempt row and no audit event at all, which
 *   made the trail's own claim false for exactly the attempts an inspection is
 *   most likely to ask about. An attempt refused for this reason carries no
 *   citation: no record was read, and inventing one would state a judgement
 *   that never happened.
 */
export const PUBLICATION_RELEASE_REFUSAL_REASONS = [
    'release_state_changed',
    'prerequisite_not_satisfied',
    'no_installed_window_declared',
    'no_deposit_submission_recorded',
    'no_deposit_receipt_recorded',
    'deposit_window_not_elapsed',
    'window_not_computable',
    'not_awaiting_gate',
];
// ── The appointed-representative register (AC4) ──
/** Whether the appointed representative is an individual or an organisation. */
export const REPRESENTATIVE_SUBJECT_TYPES = ['person', 'entity'];
// ── The AI-assisted publication assessment (AC7) ──
/**
 * How an AI system reference resolved against the AI system register
 * (`AI_SYSTEM_PROFILE_COLLECTION`). Resolved on BOTH the document id and the
 * business id, because the two registers in the codebase disagree about which
 * one callers hold; `unresolved` is RECORDED rather than silently dropped, so a
 * link to a profile that has since been removed says so.
 */
export const AI_SYSTEM_LINK_RESOLUTIONS = ['document_id', 'profile_id', 'unresolved'];
/**
 * How far the read of the AI system register got (decision 6). The register may
 * legitimately be EMPTY on a fresh tenant: that is stated on the screen and
 * never blocks the assessment, because an assessment that cannot be started
 * until an unrelated register is populated is a workflow the instrument does
 * not have. A read that FAILED is a third state and is never rendered as the
 * empty one (the 62.10.1 twelve-red-registers lesson).
 */
export const AI_SYSTEM_REGISTER_STATES = [
    'resolved',
    'register_empty',
    'register_unreadable',
];
/**
 * `draft` while the assessment is being recorded, `completed` once a named
 * human approver stands behind it. There is deliberately no `approved` /
 * `rejected` pair and no verdict field: the assessment records the assessment
 * and its evidence, and says nothing about lawfulness (AC7, AC9).
 */
export const PUBLICATION_ASSESSMENT_STATUSES = ['draft', 'completed'];
// ── Collections ──
export const PUBLICATIONS_COLLECTION = 'grc_publications';
export const PUBLICATION_DEPOSIT_SUBMISSIONS_COLLECTION = 'grc_publication_deposit_submissions';
export const PUBLICATION_RELEASE_ATTEMPTS_COLLECTION = 'grc_publication_release_attempts';
export const PUBLICATION_ASSESSMENTS_COLLECTION = 'grc_publication_assessments';
export const LEGAL_REPRESENTATIVE_ENTRIES_COLLECTION = 'grc_legal_representative_entries';
// ── Error codes ──
export const PUBLISHING_ERROR_CODES = {
    PUBLICATION_NOT_FOUND: 'PUBLICATION_NOT_FOUND',
    DEPOSIT_SUBMISSION_NOT_FOUND: 'PUBLICATION_DEPOSIT_SUBMISSION_NOT_FOUND',
    ASSESSMENT_NOT_FOUND: 'PUBLICATION_ASSESSMENT_NOT_FOUND',
    REPRESENTATIVE_ENTRY_NOT_FOUND: 'LEGAL_REPRESENTATIVE_ENTRY_NOT_FOUND',
    /** HTTP 409 — the release gate refused; the attempt is recorded with its citation (AC2). */
    RELEASE_REFUSED: 'PUBLICATION_RELEASE_REFUSED',
    /** HTTP 409 — the publication is already `releasable`, so there is nothing to gate. */
    ALREADY_RELEASABLE: 'PUBLICATION_ALREADY_RELEASABLE',
    /** HTTP 400 — a category tag without the reason the person must give (AC3). */
    CATEGORY_REASON_REQUIRED: 'PUBLICATION_CATEGORY_REASON_REQUIRED',
    /** HTTP 400 — a superseding register entry without a reason (AC4). */
    REASON_REQUIRED: 'LEGAL_REPRESENTATIVE_REASON_REQUIRED',
    /** HTTP 405/409 — any attempt to edit or delete a register entry (AC4). */
    ENTRY_IMMUTABLE: 'LEGAL_REPRESENTATIVE_ENTRY_IMMUTABLE',
    /** HTTP 409 — the compare-and-set found the prior entry already superseded. */
    ENTRY_ALREADY_SUPERSEDED: 'LEGAL_REPRESENTATIVE_ENTRY_ALREADY_SUPERSEDED',
    /** HTTP 400 — completing an assessment with no named human approver (AC7). */
    APPROVER_REQUIRED: 'PUBLICATION_ASSESSMENT_APPROVER_REQUIRED',
    /** HTTP 409 — the AI system register could not be read, so the picker states nothing. */
    AI_SYSTEM_REGISTER_UNREADABLE: 'PUBLICATION_AI_SYSTEM_REGISTER_UNREADABLE',
    /** HTTP 400 — an instant without a zone offset (the 62.14.0 receipt-time contract). */
    INSTANT_WITHOUT_ZONE: 'PUBLICATION_INSTANT_WITHOUT_ZONE',
    /** HTTP 400 — an evidence entry with no attachment: a claim, not evidence (AC8). */
    EVIDENCE_ATTACHMENT_REQUIRED: 'PUBLICATION_EVIDENCE_ATTACHMENT_REQUIRED',
    /** HTTP 409 — a step recorded against a category that is not applied to the publication (Q-SR1). */
    CATEGORY_NOT_APPLIED: 'PUBLICATION_CATEGORY_NOT_APPLIED',
    /** HTTP 409 — a step the installed pack does not declare for that category (Q-SR1). */
    STEP_NOT_DECLARED: 'PUBLICATION_STEP_NOT_DECLARED',
    /** HTTP 400 — a step recording with neither an attachment nor a reference: a claim, not evidence. */
    STEP_EVIDENCE_REQUIRED: 'PUBLICATION_STEP_EVIDENCE_REQUIRED',
};
//# sourceMappingURL=publishing.js.map